import { Box, Divider, Flex, Stack, useColorModeValue as mode } from '@chakra-ui/react';
import DashboardCard from '@components/atoms/DashboardCard';
import Button, { ButtonVariants } from '@components/interactive/Button';
import Body1 from '@components/typography/Body1';
import Header from '@components/typography/Header';
import readyIcon from '@public/flaticons/calendar.svg';
import medicalFileIcon from '@public/flaticons/medical-file.png';
import Image from 'next/image';

// Formats date like "Jan 1, 2023 at 6:00 PM EDT"
const formatDatetime = (dateString: string) => {
  const date = new Date(dateString); // create a Date object with the desired date and time

  // create an options object for the Intl.DateTimeFormat function
  const options = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
  } as const;

  const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);

  const [dayMonth, year, time] = formattedDate.split(',');

  const finalDate = `${dayMonth}, ${year} at ${time}`;

  return finalDate;
};

const getCardState = ({ appointment, hasPPPdata }) => {
  if (appointment) {
    const datetimeString = formatDatetime(appointment.startTime);

    if (hasPPPdata) {
      return {
        body: `You're ready for your upcoming call with <b>${appointment.agentName}</b> on <b>${datetimeString}</b>. You can update your details here at any time.`,
        buttonText: 'Update Details',
        header: "You're all set!",
        icon: readyIcon,
      };
    }
    return {
      body: `You have a call scheduled with <b>${appointment.agentName}</b> on <b>${datetimeString}</b>. In preparation for your upcoming call please take a moment to tell us about your prescriptions, pharmacies, and providers.`,
      buttonText: 'Get Started',
      header: 'Get ready for your upcoming appointment',
      icon: readyIcon,
    };
  }

  return {
    body: 'Track your Doctors, Drugs, and Pharmacies in one easy place.',
    buttonText: 'Get Started',
    header: 'Keep your healthcare details up to date',
    icon: medicalFileIcon,
  };
};

function PreCallSurveyCard({ hasPPPdata, appointment }) {
  const { header, body, buttonText, icon } = getCardState({ appointment, hasPPPdata });

  if (hasPPPdata && !appointment) return null;

  return (
    <DashboardCard id="aep-reshop-card" bg="white">
      <Stack direction={['column', 'column', 'row']} alignItems={['stretch', 'stretch', 'normal']}>
        <Divider
          opacity={1}
          borderColor={mode('blue.500', 'blue.200')}
          borderWidth="2px"
          height="inherit"
          borderRadius={5}
          orientation="vertical"
          mr={[0, 0, 4]}
          ml={[0, 0, -5]}
          mb={[6, 6, 0]}
          flexDir="column"
        />
        <Flex w="100%" justifyContent="center" maxW={['100%', '100%', '126px']}>
          <Box
            minW={['80px', '80px', '110px']}
            minH={['80px', '80px', '110px']}
            maxWidth="120px"
            position="relative"
            display="flex"
            alignItems="center"
            mr={[0, 0, '16px !important']}
          >
            <Image src={icon} alt="AEP enrollment icon" />
          </Box>
        </Flex>
        <Stack pt={[6, 6, 0]}>
          <Header as="h4" display="inline-block">
            {header}
          </Header>
          <Body1 pb={3} dangerouslySetInnerHTML={{ __html: body }} />
          <Flex direction={['column', 'column', 'row']} gap={4}>
            <Button id="client-survey" href="/account/survey/update-zipcode" w={['100%', '100%', 200]}>
              {buttonText}
            </Button>
            {appointment && (
              <Button
                w={['100%', '100%', 200]}
                href="https://calendly.com/reschedulings/fbbd26e8-947d-4ca0-ba4b-befab9841e2e"
                id="aep-opt-out"
                variant={ButtonVariants.secondary}
              >
                Reschedule
              </Button>
            )}
          </Flex>
        </Stack>
      </Stack>
    </DashboardCard>
  );
}

export default PreCallSurveyCard;
